import React, { useEffect, useRef, useState } from 'react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

const projects = [
    {
        id: '01',
        title: 'Hub de Innovación La Paz',
        category: 'Infraestructura',
        year: '2023',
        image: '/images/projects/hub-innovacion.jpg',
        gradient: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.primary})`,
        icon: '🏢',
        description: 'Transformamos una sede histórica del centro paceño en el hub de innovación más moderno de Bolivia. 1,200 m² de espacios colaborativos, salas de reuniones equipadas con tecnología de última generación y áreas de descanso que fomentan la creatividad y el encuentro entre emprendedores.',
        tags: ['1,200 m²', 'La Paz Centro', '80 puestos'],
        accent: colors.primary,
    },
    {
        id: '02',
        title: 'Programa Nexo Emprendedor',
        category: 'Comunidad',
        year: '2022',
        image: '/images/projects/nexo-emprendedor.jpg',
        gradient: `linear-gradient(145deg, #451759, ${colors.secondary})`,
        icon: '🚀',
        description: 'Diseñamos e implementamos un programa de aceleración para startups bolivianas en etapa temprana. Durante 6 meses, 24 equipos recibieron mentoría de expertos, acceso a espacios y conexión con inversionistas ángeles. El 75% de los proyectos continúan operando hoy.',
        tags: ['24 startups', '6 meses', '18 activas hoy'],
        accent: colors.secondary,
    },
    {
        id: '03',
        title: 'Sala Summit — Eventos Corporativos',
        category: 'Espacios',
        year: '2023',
        image: '/images/projects/sala-summit.jpg',
        gradient: `linear-gradient(145deg, #61257B, ${colors.accentDark})`,
        icon: '🎯',
        description: 'Creamos la sala de eventos corporativos más sofisticada de Calacoto. Con capacidad para 120 personas, sistema de sonido profesional, pantallas LED de 4K y servicio de catering integrado. Sede de más de 60 eventos empresariales en su primer año de operación.',
        tags: ['120 personas', '60+ eventos', 'Calacoto'],
        accent: colors.accentDark,
    },
    {
        id: '04',
        title: 'Red de Cowork Bolivia',
        category: 'Expansión',
        year: '2024',
        image: '/images/projects/red-cowork.jpg',
        gradient: `linear-gradient(145deg, ${colors.primaryDark}cc, ${colors.secondary})`,
        icon: '🌐',
        description: 'Lanzamos la primera red interconectada de espacios de coworking en Bolivia, conectando sedes en La Paz, Santa Cruz y Cochabamba. Los miembros acceden a cualquier sede con una sola membresía, creando el ecosistema de trabajo flexible más grande del país.',
        tags: ['3 ciudades', '200+ miembros', 'Membresía única'],
        accent: colors.secondaryLight,
    },
    {
        id: '05',
        title: 'Banco de Talento Digital',
        category: 'Tecnología',
        year: '2024',
        image: '/images/projects/banco-talento.jpg',
        gradient: `linear-gradient(145deg, #38124a, #852166)`,
        icon: '💡',
        description: 'Desarrollamos una plataforma digital que conecta a profesionales independientes con empresas que necesitan talento especializado. Más de 350 profesionales registrados en áreas de tecnología, diseño, marketing y consultoría. La plataforma ha facilitado más de 180 contrataciones exitosas.',
        tags: ['350+ profesionales', '180 contrataciones', 'Plataforma propia'],
        accent: colors.secondary,
    },
    {
        id: '06',
        title: 'Co Marca Sustentable 2030',
        category: 'Sostenibilidad',
        year: '2024',
        image: '/images/projects/sustentable.jpg',
        gradient: `linear-gradient(145deg, #2a0f3d, ${colors.secondaryLight})`,
        icon: '🌱',
        description: 'Iniciativa de largo plazo para convertir todas nuestras sedes en espacios de carbono neutro para el año 2030. Instalación de paneles solares, sistemas de reciclaje de agua, mobiliario de materiales reciclados y programa de compensación de huella de carbono para todos nuestros miembros.',
        tags: ['Carbono neutro 2030', '3 sedes', 'Impacto medible'],
        accent: colors.secondaryLight,
    },
];

// ── Project Card ──
function ProjectCard({ project, index }: { project: typeof projects[0]; index: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [hovered, setHovered] = useState(false);
    const isEven = index % 2 === 0;

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView
                    ? (hovered ? 'translateY(-4px)' : 'translateY(0)')
                    : (isEven ? 'translateX(-40px)' : 'translateX(40px)'),
                opacity: inView ? 1 : 0,
                transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.05}s`,
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <article
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="project-card-inner"
                    style={{
                        display: 'flex',
                        flexDirection: isEven ? 'row' : 'row-reverse',
                        gap: '0',
                        borderRadius: '28px',
                        overflow: 'hidden',
                        background: isDark ? '#150d24' : '#fff',
                        border: isDark ? '1px solid rgba(193,52,139,0.25)' : '1.5px solid #f1f5f9',
                        boxShadow: hovered
                            ? `0 24px 64px ${project.accent}18, 0 8px 32px rgba(0,0,0,0.08)`
                            : '0 4px 24px rgba(0,0,0,0.05)',
                        transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                        flexWrap: 'wrap',
                        width: '100%',
                    }}
                >
                    {/* Image side */}
                    <div className="project-img-side" style={{
                        flex: '0 0 420px',
                        minHeight: '340px',
                        position: 'relative',
                        overflow: 'hidden',
                        background: project.gradient,
                    }}>
                        <img
                            src={project.image}
                            alt={project.title}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)',
                                transform: hovered ? 'scale(1.05)' : 'scale(1)',
                            }}
                            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                        {/* Overlay decorativo */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: project.gradient,
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            gap: '12px',
                        }}>
                            <div style={{
                                position: 'absolute', top: '-40px', right: '-40px',
                                width: '200px', height: '200px',
                                background: 'rgba(255,255,255,0.07)', borderRadius: '50%',
                            }} />
                            <div style={{
                                position: 'absolute', bottom: '-50px', left: '-30px',
                                width: '220px', height: '220px',
                                background: 'rgba(255,255,255,0.04)', borderRadius: '50%',
                            }} />
                            <div style={{ fontSize: '4.5rem', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.2))' }}>
                                {project.icon}
                            </div>
                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255,255,255,0.25)',
                                borderRadius: '100px', padding: '6px 16px',
                                fontSize: '0.78rem', fontWeight: '700',
                                color: '#fff', letterSpacing: '0.06em',
                            }}>
                                {project.category}
                            </div>
                        </div>

                        {/* Número del proyecto */}
                        <div style={{
                            position: 'absolute',
                            top: '20px',
                            left: isEven ? 'auto' : '20px',
                            right: isEven ? '20px' : 'auto',
                            fontSize: '5rem', fontWeight: '900',
                            color: 'rgba(255,255,255,0.12)',
                            lineHeight: 1, letterSpacing: '-0.04em',
                            fontFamily: "'DM Sans', sans-serif",
                        }}>
                            {project.id}
                        </div>

                        {/* Año badge */}
                        <div style={{
                            position: 'absolute', bottom: '20px',
                            left: isEven ? '20px' : 'auto',
                            right: isEven ? 'auto' : '20px',
                            background: 'rgba(0,0,0,0.35)',
                            backdropFilter: 'blur(8px)',
                            borderRadius: '8px', padding: '5px 12px',
                            fontSize: '0.75rem', fontWeight: '700',
                            color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em',
                        }}>
                            {project.year}
                        </div>
                    </div>

                    {/* Content side */}
                    <div className="project-content-side" style={{
                        flex: 1,
                        padding: '44px 48px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        minWidth: '280px',
                    }}>
                        {/* Category + line */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                            <span style={{
                                fontSize: '0.7rem', fontWeight: '700',
                                letterSpacing: '0.14em', textTransform: 'uppercase',
                                color: isDark ? '#FFD97C' : project.accent,
                            }}>
                                {project.category}
                            </span>
                            <div style={{ height: '2px', width: '32px', borderRadius: '2px', background: isDark ? '#FFD97C' : project.accent }} />
                        </div>

                        {/* Title */}
                        <h2 style={{
                            fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                            fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.2', letterSpacing: '-0.02em',
                            marginBottom: '16px',
                            fontFamily: "'DM Sans', sans-serif",
                        }}>
                            {project.title}
                        </h2>

                        {/* Description */}
                        <p style={{
                            fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#475569',
                            lineHeight: '1.85', marginBottom: '28px',
                            maxWidth: '480px',
                        }}>
                            {project.description}
                        </p>

                        {/* Tags */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {project.tags.map(tag => (
                                <span key={tag} style={{
                                    background: isDark ? 'rgba(255,255,255,0.06)' : `${project.accent}12`,
                                    border: isDark ? '1px solid rgba(255,255,255,0.14)' : `1.5px solid ${project.accent}28`,
                                    borderRadius: '100px', padding: '5px 14px',
                                    fontSize: '0.78rem', fontWeight: '600',
                                    color: isDark ? '#FFD97C' : project.accent,
                                    letterSpacing: '0.02em',
                                }}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </article>
            </BorderGlow>
        </div>
    );
}

// ── Hero section ──
function HeroSection() {
    const { isDark } = useTheme();
    const [headerRef, headerInView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setForced(true), 100);
        return () => clearTimeout(t);
    }, []);
    const visible = headerInView || forced;

    return (
        <section style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #faf6fb 55%, #f6ecf5 100%)',
            padding: 'clamp(100px, 13vw, 140px) 32px clamp(50px, 7vw, 70px)',
            position: 'relative', overflow: 'hidden',
        }}>
            {/* Blob animado */}
            <div className="blob-anim" style={{
                position: 'absolute', top: '-80px', right: '-80px',
                width: '420px', height: '420px',
                background: isDark
                    ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))'
                    : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))',
                pointerEvents: 'none',
            }} />
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />

            <div ref={headerRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                {/* Título principal */}
                <h1 className={`fade-up-proj d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                    fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                    lineHeight: '1.08', letterSpacing: '-0.025em',
                    marginBottom: '12px',
                    fontFamily: "'DM Sans', sans-serif",
                }}>
                    Nuestros <span style={{
                        background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>Proyectos</span>
                </h1>

                {/* Subtítulo */}
                <h2 className={`fade-up-proj d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                    fontWeight: '600', color: isDark ? '#cbd5e1' : '#475569',
                    letterSpacing: '-0.01em', marginBottom: '20px',
                    fontFamily: "'DM Sans', sans-serif",
                }}>
                    Lo que hemos <span style={{ color: isDark ? '#FFD97C' : colors.secondary }}>construido juntos</span>
                </h2>

                {/* Descripción */}
                <p className={`fade-up-proj d2 ${visible ? 'in' : ''}`} style={{
                    fontSize: '1.05rem', color: isDark ? '#94a3b8' : '#64748b',
                    maxWidth: '560px', margin: '0 auto',
                    lineHeight: '1.75', fontWeight: '400',
                }}>
                    Cada proyecto refleja nuestra visión de transformar la manera en que los bolivianos trabajan, colaboran e innovan. Aquí te contamos las historias detrás de lo que hemos logrado.
                </p>

                {/* Stats */}
                <div className={`fade-up-proj d3 ${visible ? 'in' : ''}`} style={{
                    display: 'flex', justifyContent: 'center', gap: '0',
                    marginTop: '40px', flexWrap: 'wrap',
                }}>
                    {[
                        { value: '6', label: 'Proyectos destacados', color: isDark ? '#FFD97C' : colors.primary },
                        { value: '5+', label: 'Años de experiencia', color: isDark ? '#C1348B' : colors.secondary },
                        { value: '300+', label: 'Personas impactadas', color: isDark ? '#EA8406' : colors.accentDark },
                    ].map((stat, i) => (
                        <div key={stat.label} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            padding: '20px 36px',
                            borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.25)' : '1.5px solid #f1f5f9') : 'none',
                        }}>
                            <div style={{
                                fontSize: '2.6rem', fontWeight: '900',
                                color: stat.color, lineHeight: 1,
                                fontFamily: "'DM Sans', sans-serif",
                                letterSpacing: '-0.03em',
                            }}>
                                {stat.value}
                            </div>
                            <div style={{
                                fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8',
                                fontWeight: '500', marginTop: '6px',
                                letterSpacing: '0.01em',
                            }}>
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Barra de colores de marca */}
                <div className={`fade-up-proj d4 ${visible ? 'in' : ''}`} style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes variant="scroll-prompt" size="md" promptText="Descubre nuestros proyectos" />
                </div>
            </div>
        </section>
    );
}

// ── Main component ──
const AboutusProjects: React.FC = () => {
    const { isDark } = useTheme();

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            background: isDark ? '#0b0614' : '#f8fafc',
            minHeight: '100vh',
            transition: 'background 0.3s ease',
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }

        .fade-up-proj {
          opacity: 0; transform: translateY(28px);
          transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1);
        }
        .fade-up-proj.in { opacity: 1; transform: translateY(0); }
        .fade-up-proj.d1 { transition-delay: 0.1s; }
        .fade-up-proj.d2 { transition-delay: 0.2s; }
        .fade-up-proj.d3 { transition-delay: 0.3s; }
        .fade-up-proj.d4 { transition-delay: 0.42s; }

        @media (max-width: 900px) {
          .project-card-inner { flex-direction: column !important; }
          .project-img-side { flex: none !important; width: 100% !important; min-height: 260px !important; }
          .project-content-side { padding: 28px 24px !important; }
        }
      `}</style>

            <HeroSection />

            {/* Projects list */}
            <section style={{ padding: '60px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {projects.map((project, index) => (
                        <ProjectCard key={project.id} project={project} index={index} />
                    ))}
                </div>

                {/* CTA final */}
                <CtaBanner />
            </section>
        </div>
    );
};

function CtaBanner() {
    const [ref, inView] = useInView(0.2);
    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                marginTop: '64px',
                borderRadius: '28px',
                background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 60%, #C1348B 100%)`,
                padding: '56px 48px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '28px',
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.75s cubic-bezier(.16,1,.3,1)',
                position: 'relative', overflow: 'hidden',
                boxShadow: '0 20px 48px rgba(97,37,123,0.3)',
            }}
        >
            <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', bottom: '-40px', left: '20%', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
            <div style={{ position: 'relative' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>
                    ¿Tienes un proyecto?
                </div>
                <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '800', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em' }}>
                    Hagámoslo realidad juntos
                </h3>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', marginTop: '10px', maxWidth: '460px', lineHeight: '1.7' }}>
                    Co Marca Group es el ecosistema donde las mejores ideas de Bolivia encuentran el espacio, la comunidad y el impulso para crecer.
                </p>
            </div>
            <a href="/contacto" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: '#fff', color: colors.primary,
                borderRadius: '14px', padding: '14px 32px',
                fontWeight: '700', fontSize: '0.97rem',
                textDecoration: 'none', flexShrink: 0,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                transition: 'all 0.22s',
                position: 'relative',
            }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
            >
                Contáctanos →
            </a>
        </div>
    );
}

export default AboutusProjects;